import { supabase } from '../supabase';
import { SettlementDetails } from '../utils/types';

export interface SettlementShipmentAllocation {
  id?: string;
  settlement_detail_id: number;
  shipment_box_id: string;
  allocated_quantity: number;
  receiver_product_snapshot: Record<string, unknown>;
}

export interface SettlementReconciliationState {
  current_allocations: SettlementShipmentAllocation[];
  other_usage_by_shipment_box: Record<string, number>;
}

export interface ReconciledSettlementDetailInput {
  temp_key: string;
  id: number | null;
  source_detail_id: number;
  variety: string;
  caliber: string;
  color: string;
  format: string;
  label: string;
  category: string;
  pallet: string;
  unit_weight: number;
  quantity: number;
  kilos: number;
  local_total_price: number;
  local_total_tax: number;
  local_total_commision: number;
  local_other_costs: number;
  local_repacking_costs: number;
  usd_freight: number;
  usd_total_fob: number;
}

export interface ReconciledShipmentAllocationInput {
  temp_key: string;
  shipment_box_id: string;
  allocated_quantity: number;
  receiver_product_snapshot: Record<string, unknown>;
}

export interface ApplySettlementReconciliationResult {
  reconciliation_id: string;
  details: SettlementDetails[];
}

export const getSettlementReconciliationState = async (
  settlementId: number,
  shipmentBoxIds: string[]
): Promise<SettlementReconciliationState> => {
  const numericShipmentBoxIds = shipmentBoxIds
    .map(Number)
    .filter(Number.isFinite);
  const { data, error } = await supabase.rpc(
    'get_settlement_reconciliation_allocations',
    {
      p_settlement_id: settlementId,
      p_shipment_box_ids: numericShipmentBoxIds,
    }
  );

  if (error) throw error;

  const result = (data || {}) as Partial<SettlementReconciliationState>;
  return {
    current_allocations: result.current_allocations || [],
    other_usage_by_shipment_box: result.other_usage_by_shipment_box || {},
  };
};

export const applySettlementReconciliation = async (params: {
  settlementId: number;
  details: ReconciledSettlementDetailInput[];
  allocations: ReconciledShipmentAllocationInput[];
  // Shipment boxes that are not shipment_boxes rows (Frutic): rewrite the
  // lines without persisting box allocations.
  externalShipmentBoxes?: boolean;
}): Promise<ApplySettlementReconciliationResult> => {
  const { data, error } = await supabase.rpc(
    'apply_settlement_reconciliation',
    {
      p_settlement_id: params.settlementId,
      p_details: params.details,
      p_allocations: params.allocations,
      // Only sent when needed so non-Frutic calls also work before the
      // 20260930160000 migration is applied.
      ...(params.externalShipmentBoxes ? { p_external_boxes: true } : {}),
    }
  );

  if (error) throw error;
  return data as ApplySettlementReconciliationResult;
};
